import { useEffect } from 'react';
import { Button, Form, Input, Select, Space, Switch } from 'antd';
import type { FormInstance } from 'antd';
import { Permission } from '@aid/shared';
import type { UserResponse } from '../../../api/types';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { useRolesList } from '../../roles/api/useRoles';

export const USER_FORM_MODE = {
  CREATE: 'create',
  EDIT: 'edit',
} as const;

export type UserFormMode = (typeof USER_FORM_MODE)[keyof typeof USER_FORM_MODE];

export interface UserFormValues {
  email?: string;
  username?: string;
  password?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  jobTitle?: string;
  department?: string;
  isActive?: boolean;
  roleIds?: string[];
}

interface UserFormProps {
  mode: UserFormMode;
  initialValues?: UserResponse | null;
  form?: FormInstance<UserFormValues>;
  onSubmit: (values: UserFormValues) => void | Promise<void>;
  loading?: boolean;
  onCancel?: () => void;
  showActions?: boolean;
}

function ActiveRolesField() {
  const t = useT();
  const { data: rolesData, isLoading } = useRolesList({ limit: 100, isActive: true });
  const activeRoles = (rolesData?.data ?? []).filter((role) => role.isActive);

  return (
    <Form.Item name="roleIds" label={t('pages.users.roles')}>
      <Select
        mode="multiple"
        allowClear
        loading={isLoading}
        placeholder={t('pages.users.rolesPlaceholder')}
        options={activeRoles.map((role) => ({ label: role.name, value: role.id }))}
      />
    </Form.Item>
  );
}

export function UserForm({
  mode,
  initialValues,
  form: providedForm,
  onSubmit,
  loading = false,
  onCancel,
  showActions = true,
}: UserFormProps) {
  const t = useT();
  const [internalForm] = Form.useForm<UserFormValues>();
  const form = providedForm ?? internalForm;
  const isCreate = mode === USER_FORM_MODE.CREATE;
  const canReadRoles = useHasPermission(Permission.ROLES_READ);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(
      initialValues
        ? {
            firstName: initialValues.firstName ?? '',
            lastName: initialValues.lastName ?? '',
            phone: initialValues.phone ?? '',
            jobTitle: initialValues.jobTitle ?? '',
            department: initialValues.department ?? '',
            isActive: initialValues.isActive,
          }
        : { isActive: true },
    );
  }, [form, initialValues, mode]);

  return (
    <Form<UserFormValues>
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      preserve={false}
      clearOnDestroy
      style={{ width: '100%' }}
    >
      {isCreate && (
        <>
          <Form.Item
            name="email"
            label={t('pages.users.email')}
            rules={[
              { required: true, message: t('pages.users.emailRequired') },
              { type: 'email', message: t('pages.users.invalidEmail') },
            ]}
          >
            <Input autoComplete="email" />
          </Form.Item>
          <Form.Item
            name="username"
            label={t('pages.users.username')}
            rules={[{ required: true, message: t('pages.users.usernameRequired') }]}
          >
            <Input autoComplete="username" />
          </Form.Item>
          <Form.Item
            name="password"
            label={t('pages.users.password')}
            rules={[
              { required: true, message: t('pages.users.passwordRequired') },
              { min: 6, message: t('pages.users.passwordMin') },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          {canReadRoles && <ActiveRolesField />}
        </>
      )}
      <Form.Item
        name="firstName"
        label={t('pages.users.firstName')}
        rules={[{ required: true, message: t('pages.users.firstNameRequired') }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="lastName"
        label={t('pages.users.lastName')}
        rules={[{ required: true, message: t('pages.users.lastNameRequired') }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="phone" label={t('common.phone')}>
        <Input />
      </Form.Item>
      <Form.Item name="jobTitle" label={t('pages.users.jobTitle')}>
        <Input />
      </Form.Item>
      <Form.Item name="department" label={t('pages.users.department')}>
        <Input />
      </Form.Item>
      {!isCreate && (
        <Form.Item name="isActive" label={t('pages.users.active')} valuePropName="checked">
          <Switch />
        </Form.Item>
      )}
      {showActions && (
        <Form.Item>
          <Space wrap size={[8, 8]}>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t(isCreate ? 'pages.users.submitCreate' : 'pages.users.submitEdit')}
            </Button>
            {onCancel && (
              <Button onClick={onCancel} disabled={loading}>
                {t('common.cancel')}
              </Button>
            )}
          </Space>
        </Form.Item>
      )}
    </Form>
  );
}
